/**
 * Detect when a user's chat message asks the assistant to generate a downloadable
 * document (e.g. "génère un doc .docx", "generate a .md summary"). When detected,
 * the assistant reply is rendered as a download card instead of raw text.
 *
 * Kept in sync with the backend detector
 * `_detect_document_request_format` in
 * backend/app/services/knowledge_engine/conversations/chat_orchestrator_service.py
 * Only the client-side exportable formats (md/docx/pdf) are considered here.
 */

export type DocumentRequestFormat = 'md' | 'docx' | 'pdf';

// A generation verb: génère/crée/rédige/produis/prépare/export/télécharge, plus
// English generate/create/write/draft and the "fais-moi / donne-moi / make me" forms.
const VERB_PATTERN =
  /(g[eéè]n[eéè]r|cr[eéè]e|r[eéè]dig|produi|pr[eéè]par|export|t[eéè]l[eéè]charg|generat|creat|writ|draft|fais[-\s]?moi|donne[-\s]?moi|make me|give me)/i;

// Explicit format token: ".md" / ".docx" / ".pdf" or the words markdown/word/pdf.
const FORMAT_PATTERN = /(\.(md|docx|pdf)\b|\b(markdown|word|pdf)\b)/i;

// A "file" noun that signals a deliverable is wanted (not just an inline answer).
const NOUN_PATTERN = /\b(doc|document|fichier|rapport|livrable|deliverable)\b/i;

/**
 * Returns the requested document format, or null when the message is not a
 * document-generation request. Requires a generation verb AND either an explicit
 * format token or a document noun, so ordinary questions don't trigger.
 */
export function detectDocumentRequest(message?: string | null): DocumentRequestFormat | null {
  const text = (message || '').trim();
  if (!text || !VERB_PATTERN.test(text)) return null;

  const formatMatch = FORMAT_PATTERN.exec(text);
  if (!formatMatch && !NOUN_PATTERN.test(text)) return null;

  if (formatMatch) {
    const ext = (formatMatch[2] || '').toLowerCase();
    if (ext === 'md' || ext === 'docx' || ext === 'pdf') return ext;
    const word = (formatMatch[3] || '').toLowerCase();
    if (word === 'word') return 'docx';
    if (word === 'markdown') return 'md';
    if (word === 'pdf') return 'pdf';
  }
  // Generic document request without an explicit format → default to docx.
  return 'docx';
}
